import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  BellIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import moment from "moment-timezone";
import {
  fetchMemberLinkNotifications,
  markNotificationsAsRead,
} from "services/memberMrn";
import { useSocketEvent, Events } from "services/socket";
import { Dialog, DialogTitle, DialogBody } from "components/dialog";
import { DateRangePicker } from "components/DateRangePicker";

function toDayBoundaryIso(date, timeZone, boundary) {
  if (!date) return undefined;
  const dateOnly = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
  const zone = timeZone || moment.tz.guess();
  const parsed = moment.tz(dateOnly, "YYYY-MM-DD", zone);
  return boundary === "end"
    ? parsed.endOf("day").toISOString()
    : parsed.startOf("day").toISOString();
}

/**
 * NotificationModal & Bell Trigger Component
 *
 * Features:
 * - Default 1-day range (Today) showing all read & unread notifications
 * - Date Range Picker to inspect past / older notifications
 * - Real-time WebSocket live updates
 * - Unread / All filter tabs
 * - Reverse-chronological ordering (newest first)
 */
export default function NotificationModal({
  fundingsourceId,
  mrn,
  timeZone,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterUnread, setFilterUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Default date filter is Today [today, today]
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    return [today, today];
  });
  const [startDate, endDate] = dateRange;

  // Load notifications for the selected date range and filter
  const loadNotifications = useCallback(
    async (isBackground = false) => {
      if (!fundingsourceId || !mrn) return;

      if (!isBackground) setLoading(true);

      try {
        const startIso = toDayBoundaryIso(startDate, timeZone, "start");
        const endIso = toDayBoundaryIso(endDate || startDate, timeZone, "end");

        const data = await fetchMemberLinkNotifications(fundingsourceId, {
          mrn,
          startDate: startIso,
          endDate: endIso,
          unread: filterUnread ? true : false,
          skip: 0,
          limit: 100,
        });

        const list = Array.isArray(data)
          ? data
          : data?.notifications || data?.data || [];

        setNotifications(list);

        // Count unread
        const unread = list.filter((item) => item.unread || !item.isRead).length;
        setUnreadCount(unread);
      } catch (err) {
        console.error("[NotificationModal] Error loading notifications:", err);
      } finally {
        if (!isBackground) setLoading(false);
      }
    },
    [fundingsourceId, mrn, filterUnread, startDate, endDate, timeZone]
  );

  // Fetch notifications when dateRange or filter changes
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Real-time Socket Event: Prepend incoming notification
  useSocketEvent(
    Events.MEMBER_LINK_NOTIFICATION,
    (payload) => {
      if (!payload) return;

      const newNotification = {
        _id: payload._id || payload.memberAssign || String(Date.now()),
        action: payload.action || "received",
        tripId: payload.tripId || "Trip Update",
        message: payload.message,
        intakeStatus: payload.intakeStatus,
        scheduleTime: payload.scheduleTime,
        createdAt: payload.createdAt || new Date().toISOString(),
        unread: true,
        isRead: false,
        ...payload,
      };

      setNotifications((prev) => {
        const filtered = prev.filter(
          (item) =>
            (item._id || item.tripId) !== (newNotification._id || newNotification.tripId)
        );
        return [newNotification, ...filtered];
      });
      setUnreadCount((prev) => prev + 1);
    },
    [fundingsourceId, mrn]
  );

  // Strict reverse-chronological ordering (Newest on Top)
  const orderedNotifications = useMemo(() => {
    let list = [...notifications];
    if (filterUnread) {
      list = list.filter((item) => item.unread || !item.isRead);
    }
    return list.sort((a, b) => {
      const timeA = new Date(
        a.createdAt || a.timestamp || a.scheduleTime || 0
      ).getTime();
      const timeB = new Date(
        b.createdAt || b.timestamp || b.scheduleTime || 0
      ).getTime();
      return timeB - timeA;
    });
  }, [notifications, filterUnread]);

  const resetToToday = () => {
    const today = new Date();
    setDateRange([today, today]);
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter((item) => item.unread || !item.isRead)
      .map((item) => item._id)
      .filter(Boolean);

    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((item) => ({ ...item, unread: false, isRead: true }))
    );
    setUnreadCount(0);

    if (fundingsourceId && unreadIds.length > 0) {
      try {
        await markNotificationsAsRead(fundingsourceId, unreadIds);
      } catch (err) {
        console.error("[NotificationModal] Error marking all as read:", err);
      }
    }
  };

  const markSingleAsRead = async (id) => {
    if (!id) return;

    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((item) =>
        item._id === id ? { ...item, unread: false, isRead: true } : item
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    if (fundingsourceId) {
      try {
        await markNotificationsAsRead(fundingsourceId, [id]);
      } catch (err) {
        console.error("[NotificationModal] Error marking single notification as read:", err);
      }
    }
  };

  // Matches the exact status badge styles from TripsListing
  const getStatusBadge = (action = "") => {
    const act = (action || "").toLowerCase();
    if (act.includes("cancel")) {
      return {
        label: "Cancelled",
        className: "bg-red-50 text-red-700 ring-1 ring-red-600/10",
        icon: XCircleIcon,
      };
    }
    if (act.includes("confirm") || act.includes("accept")) {
      return {
        label: "Confirmed",
        className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10",
        icon: CheckCircleIcon,
      };
    }
    if (act.includes("complete") || act.includes("finish")) {
      return {
        label: "Finished",
        className: "bg-gray-50 text-gray-600 ring-1 ring-gray-500/10",
        icon: CheckCircleIcon,
      };
    }
    if (act.includes("onboard") || act.includes("onway") || act.includes("arrived")) {
      return {
        label: "In Progress",
        className: "bg-sky-50 text-sky-700 ring-1 ring-sky-700/10",
        icon: TruckIcon,
      };
    }
    if (act.includes("received") || act.includes("intake")) {
      return {
        label: "Intake",
        className: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-700/10",
        icon: TruckIcon,
      };
    }
    if (act.includes("unconfirmed")) {
      return {
        label: "Unconfirmed",
        className: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/15",
        icon: InformationCircleIcon,
      };
    }
    return {
      label: action.toUpperCase() || "Update",
      className: "bg-purple-50 text-purple-700 ring-1 ring-purple-700/10",
      icon: InformationCircleIcon,
    };
  };

  const isToday =
    startDate &&
    endDate &&
    startDate.toDateString() === new Date().toDateString() &&
    endDate.toDateString() === new Date().toDateString();

  return (
    <>
      {/* Bell Trigger Button */}
      <button
        type="button"
        onClick={() => {
          setIsOpen(true);
          loadNotifications();
        }}
        className="relative shrink-0 rounded-xl bg-gray-100 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition focus:outline-none focus:ring-2 focus:ring-[#293562]"
        title="Notifications"
        aria-label="Notifications"
      >
        <BellIcon className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Modal */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        size="md"
        className="max-h-[88vh] flex flex-col p-6 rounded-3xl"
      >
        {/* Header */}
        <DialogTitle className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#293562]/10 text-[#293562] flex items-center justify-center font-bold">
              <BellIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
              <p className="text-xs text-gray-500">Member MRN: {mrn}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => loadNotifications()}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
              title="Refresh"
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${loading ? "animate-spin text-[#293562]" : ""}`}
              />
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </DialogTitle>

        {/* Date Picker & Filter Controls */}
        <div className="pt-3 pb-2 space-y-2.5">
          {/* Date Picker Row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-1">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => setDateRange([start, end])}
                placeholder="Pick date range"
                className="w-full rounded-xl bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#293562] focus:bg-white transition"
              />
              {!isToday && (
                <button
                  type="button"
                  onClick={resetToToday}
                  className="shrink-0 text-xs px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition"
                >
                  Today
                </button>
              )}
            </div>
          </div>

          {/* Tab Filter & Mark All Read */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
              <button
                type="button"
                onClick={() => setFilterUnread(false)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
                  !filterUnread
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterUnread(true)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${
                  filterUnread
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="h-3.5 min-w-[0.875rem] px-1 bg-red-600 text-white rounded-full text-[9px] font-bold inline-flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="text-xs font-semibold text-[#293562] hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <DialogBody className="overflow-y-auto max-h-[50vh] py-2 space-y-2.5">
          {loading && notifications.length === 0 ? (
            <div className="py-12 text-center text-gray-400 space-y-2">
              <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-[#293562]" />
              <p className="text-xs">Loading notifications...</p>
            </div>
          ) : orderedNotifications.length === 0 ? (
            <div className="py-12 text-center text-gray-400 space-y-2">
              <CheckCircleIcon className="h-10 w-10 mx-auto text-gray-300" />
              <p className="text-sm font-medium text-gray-600">No notifications</p>
              <p className="text-xs text-gray-400">
                {filterUnread
                  ? "No unread notifications for the selected date."
                  : "No notifications found for this date range."}
              </p>
            </div>
          ) : (
            orderedNotifications.map((item, idx) => {
              const action = item.action || "UPDATE";
              const tripId = item.tripId || item.memberAssign || "Trip";
              const intakeStatus = item.intakeStatus;
              const scheduleTime = item.scheduleTime;
              const createdAt = item.createdAt || item.timestamp;
              const isUnread = item.unread || !item.isRead;
              const badge = getStatusBadge(action);

              const timeRelative = createdAt
                ? moment(createdAt).fromNow()
                : "Just now";

              return (
                <div
                  key={item._id || idx}
                  onClick={() => isUnread && markSingleAsRead(item._id)}
                  className={`p-3.5 rounded-2xl border transition-all cursor-pointer space-y-2 ${
                    isUnread
                      ? "bg-[#293562]/[0.03] border-2 border-[#293562]/20 shadow-sm"
                      : "bg-white border-gray-100 hover:border-gray-200"
                  }`}
                >
                  {/* Top Row: Status Badge & Trip ID */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                      <span className="text-xs font-bold text-gray-900 font-mono">
                        {tripId}
                      </span>
                    </div>
                    {isUnread && (
                      <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
                    )}
                  </div>

                  {/* Body Content */}
                  {item.message ? (
                    <p className="text-xs text-gray-600 leading-relaxed">{item.message}</p>
                  ) : (
                    <div className="text-xs text-gray-600 space-y-1">
                      {intakeStatus && (
                        <div className="text-gray-500">
                          Intake Status:{" "}
                          <span className="font-semibold text-gray-800 capitalize">
                            {intakeStatus.replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                      {scheduleTime && (
                        <div className="text-gray-500 flex items-center gap-1.5">
                          <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          <span>
                            Scheduled:{" "}
                            <span className="text-gray-800 font-medium">
                              {moment(scheduleTime).format(
                                "MMM DD, YYYY • hh:mm A"
                              )}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bottom: Timestamp */}
                  <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 text-[11px] text-gray-400 font-medium">
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-3 w-3 text-gray-400" />
                      <span>
                        {createdAt
                          ? moment(createdAt).format("MMM DD, YYYY • hh:mm A")
                          : "Just now"}
                      </span>
                    </div>
                    <span className="text-gray-500 font-semibold">{timeRelative}</span>
                  </div>
                </div>
              );
            })
          )}
        </DialogBody>
      </Dialog>
    </>
  );
}
